import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import UserCard from './UserCard';
import SearchAndFilters from './SearchAndFilters';
import HiddenUsersSection from './HiddenUsersSection';
import { UsersIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { hideUser, unhideUser, getHiddenUsers } from '../../../services/firebase';
import { toast } from 'react-hot-toast';

/**
 * PeopleDirectory component - Main container for the network directory feature
 * Handles responsive grid layout, loading states, error states, empty states, search functionality,
 * performance optimizations including pagination and memoization, and admin user hiding functionality
 */
const PeopleDirectory = ({ 
  users = [], 
  loading = false, 
  error = null, 
  onRetry = null,
  isAdmin = false,
  adminEmail = null,
  onUsersUpdate = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ 
    locations: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Admin-specific state
  const [hiddenUsers, setHiddenUsers] = useState([]);
  const [hiddenUsersLoading, setHiddenUsersLoading] = useState(false);
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  
  // Performance optimization constants
  const ITEMS_PER_PAGE = 20; // Show 20 users per page
  const INFINITE_SCROLL_THRESHOLD = 200; // Pixels from bottom to trigger load more
  
  // Refs for infinite scroll
  const observerRef = useRef();
  const loadMoreTriggerRef = useRef();

  // Fetch hidden users for admin
  const fetchHiddenUsers = useCallback(async () => {
    if (!isAdmin || !adminEmail) return;
    
    try {
      setHiddenUsersLoading(true);
      const result = await getHiddenUsers(adminEmail);
      
      if (result.success) {
        setHiddenUsers(result.data);
      } else {
        console.error('Failed to fetch hidden users:', result.error);
        toast.error('Failed to load hidden users');
      }
    } catch (error) {
      console.error('Error fetching hidden users:', error);
      toast.error('Error loading hidden users');
    } finally {
      setHiddenUsersLoading(false);
    }
  }, [isAdmin, adminEmail]);

  // Handle hiding a user (admin only)
  const handleHideUser = useCallback(async (userId) => {
    if (!isAdmin || !adminEmail || adminActionLoading) return;
    
    try {
      setAdminActionLoading(true);
      const result = await hideUser(userId, adminEmail);
      
      if (result.success) {
        toast.success('User hidden successfully');
        // Refresh both visible users and hidden users
        if (onUsersUpdate) {
          await onUsersUpdate();
        }
        await fetchHiddenUsers();
      } else {
        toast.error(result.error || 'Failed to hide user');
      }
    } catch (error) {
      console.error('Error hiding user:', error);
      toast.error('Error hiding user');
    } finally {
      setAdminActionLoading(false);
    }
  }, [isAdmin, adminEmail, adminActionLoading, onUsersUpdate, fetchHiddenUsers]);

  // Handle unhiding a user (admin only)  
  const handleUnhideUser = useCallback(async (userId) => {
    if (!isAdmin || !adminEmail || adminActionLoading) return;
    
    try {
      setAdminActionLoading(true);
      const result = await unhideUser(userId, adminEmail);
      
      if (result.success) {
        toast.success('User unhidden successfully');
        // Refresh both visible users and hidden users
        if (onUsersUpdate) {
          await onUsersUpdate();
        }
        await fetchHiddenUsers();
      } else {
        toast.error(result.error || 'Failed to unhide user');
      }
    } catch (error) {
      console.error('Error unhiding user:', error);
      toast.error('Error unhiding user');
    } finally {
      setAdminActionLoading(false);
    }
  }, [isAdmin, adminEmail, adminActionLoading, onUsersUpdate, fetchHiddenUsers]);

  // Fetch hidden users when admin status changes
  useEffect(() => {
    if (isAdmin && adminEmail) {
      fetchHiddenUsers();
    }
  }, [isAdmin, adminEmail, fetchHiddenUsers]);

  // Memoized search function for better performance
  const searchUsers = useCallback((userList, searchTerm) => {
    if (!searchTerm.trim()) return userList;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return userList.filter(user => {
      const firstName = user.basicInfo?.firstName?.toLowerCase() || '';
      const lastName = user.basicInfo?.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const company = user.basicInfo?.companyName?.toLowerCase() || '';
      const role = user.basicInfo?.currentRole?.toLowerCase() || '';
      const email = user.basicInfo?.email?.toLowerCase() || '';
      
      return (
        fullName.includes(searchLower) ||
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        company.includes(searchLower) ||
        role.includes(searchLower) ||
        email.includes(searchLower)
      );
    });
  }, []);

  // Memoized filter function for better performance
  const filterUsers = useCallback((userList, filters) => {
    // If no filters are applied, return all users
    if (!filters.locations?.length) {
      return userList;
    }
    
    return userList.filter(user => {
      const userLocation = user.basicInfo?.location || '';
      
      // Check location filters - simple exact match
      const locationMatch = !filters.locations?.length || filters.locations.some(filterLocation => {
        // Simple exact match (case-insensitive)
        return userLocation.toLowerCase() === filterLocation.toLowerCase() ||
               (filterLocation.toLowerCase() === 'remote' && 
                (userLocation.toLowerCase().includes('remote') || userLocation.toLowerCase().includes('anywhere')));
      });
      
      return locationMatch;
    });
  }, []);

  // Filter users based on search term and filters with memoization
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    // Apply search filter
    filtered = searchUsers(filtered, searchTerm);
    
    // Apply location, company, and role filters
    filtered = filterUsers(filtered, filters);

    return filtered;
  }, [users, searchTerm, filters, searchUsers, filterUsers]);

  // Paginated users for display with memoization
  const paginatedUsers = useMemo(() => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, ITEMS_PER_PAGE]);

  // Check if there are more users to load
  const hasMoreUsers = useMemo(() => {
    return paginatedUsers.length < filteredUsers.length;
  }, [paginatedUsers.length, filteredUsers.length]);

  // Handle search term changes with pagination reset
  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset pagination when search changes
  }, []);

  // Handle filter changes with pagination reset
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset pagination when filters change
  }, []);

  // Load more users function for infinite scroll
  const loadMoreUsers = useCallback(async () => {
    if (isLoadingMore || !hasMoreUsers) return;
    
    setIsLoadingMore(true);
    
    // Simulate loading delay for better UX (remove in production if not needed)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setCurrentPage(prevPage => prevPage + 1);
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMoreUsers]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const currentTrigger = loadMoreTriggerRef.current;
    
    if (!currentTrigger || !hasMoreUsers) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore) {
          loadMoreUsers();
        }
      },
      {
        rootMargin: `${INFINITE_SCROLL_THRESHOLD}px`,
        threshold: 0.1
      }
    );

    observer.observe(currentTrigger);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMoreUsers, isLoadingMore, loadMoreUsers, INFINITE_SCROLL_THRESHOLD]);

  // Reset pagination when users data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [users]);
  // Loading state with skeleton cards
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Search Component - disabled during loading */}
        <SearchAndFilters
          searchTerm=""
          onSearchChange={() => {}}
          filters={{}}
          onFilterChange={() => {}}
          users={users}
          className="opacity-50 pointer-events-none"
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 
                        gap-4 sm:gap-6 lg:gap-8">
            {/* Render 8 skeleton cards */}
            {Array.from({ length: 8 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry functionality
  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 sm:p-12">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>
          
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Unable to Load Members Directory
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-8 text-sm sm:text-base leading-relaxed">
            {error || 'Something went wrong while loading the members directory. Please try again.'}
          </p>
          
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 
                       text-white font-medium rounded-lg 
                       hover:shadow-lg hover:-translate-y-0.5
                       transition-all duration-200 ease-out
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                       dark:focus:ring-offset-gray-800"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state when no users are found
  if (!users || users.length === 0) {
    return (
      <div className="space-y-8">
        {/* Search Component - still show even when no users */}
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          users={users}
        />
        
        <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 sm:p-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full">
                <UsersIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              No People Found
            </h3>
            
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
              There are currently no users to display in the directory. Check back later as more people join the platform.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main directory view with search and responsive grid
  return (
    <div className="space-y-6">
      {/* Search and Filters Component */}
      <SearchAndFilters
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        users={users}
      />

      {/* No search/filter results found */}
      {(searchTerm || filters.locations?.length > 0) && filteredUsers.length === 0 ? (
        <div className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 sm:p-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full">
                <UsersIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              No Results Found
            </h3>
            
            <p className="text-gray-600 dark:text-gray-300 mb-8 text-sm sm:text-base leading-relaxed">
              {searchTerm && filters.locations?.length > 0 ? (
                <>No people match your search for "<span className="font-medium text-gray-800 dark:text-gray-200">{searchTerm}</span>" with the selected filters. Try adjusting your search terms or filters.</>
              ) : searchTerm ? (
                <>No people match your search for "<span className="font-medium text-gray-800 dark:text-gray-200">{searchTerm}</span>". Try adjusting your search terms.</>
              ) : (
                <>No people match the selected filters. Try adjusting your filter selections.</>
              )}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 
                           text-white font-medium rounded-lg 
                           hover:shadow-lg hover:-translate-y-0.5
                           transition-all duration-200 ease-out
                           focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                           dark:focus:ring-offset-gray-800"
                >
                  Clear Search
                </button>
              )}
              {filters.locations?.length > 0 && (
                <button
                  onClick={() => setFilters({ locations: [] })}
                  className="inline-flex items-center px-6 py-3 bg-gray-600 hover:bg-gray-700 
                           text-white font-medium rounded-lg 
                           hover:shadow-lg hover:-translate-y-0.5
                           transition-all duration-200 ease-out
                           focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 
                           dark:focus:ring-offset-gray-800"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Responsive grid layout with consistent card heights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 
                        gap-4 sm:gap-6 lg:gap-8 
                        auto-rows-fr">
            {paginatedUsers.map((user) => (
              <MemoizedUserCard 
                key={user.uid || user.id} 
                user={user}
                isAdmin={isAdmin}
                onHideUser={handleHideUser}
                onUnhideUser={handleUnhideUser}
                isHidden={false}
              />
            ))}
          </div>
          
          {/* Infinite scroll trigger and loading more indicator */}
          {hasMoreUsers && (
            <div 
              ref={loadMoreTriggerRef}
              className="mt-8 flex justify-center"
            >
              {isLoadingMore ? (
                <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <span className="text-sm font-medium">Loading more people...</span>
                </div>
              ) : (
                <button
                  onClick={loadMoreUsers}
                  className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 
                           border border-gray-300 dark:border-gray-600 rounded-lg
                           text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                           hover:border-gray-400 dark:hover:border-gray-500
                           focus:outline-none focus:ring-2 focus:ring-purple-500 
                           shadow-sm hover:shadow-md focus:shadow-md
                           transition-all duration-200 ease-out"
                >
                  <span className="text-sm font-medium">Load More People</span>
                  <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          )}
          
          {/* Results count */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                {searchTerm || filters.locations?.length > 0 ? (
                  <>
                    {hasMoreUsers ? (
                      <>
                        Showing <span className="text-purple-600 dark:text-purple-400">{paginatedUsers.length}</span> of <span className="text-purple-600 dark:text-purple-400">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'person' : 'people'}
                        {searchTerm && <> matching "<span className="font-semibold">{searchTerm}</span>"</>}
                        {filters.locations?.length > 0 && <> with selected filters</>}
                      </>
                    ) : (
                      <>
                        Found <span className="text-purple-600 dark:text-purple-400">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'person' : 'people'}
                        {searchTerm && <> matching "<span className="font-semibold">{searchTerm}</span>"</>}
                        {filters.locations?.length > 0 && <> with selected filters</>}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {hasMoreUsers ? (
                      <>Showing <span className="text-purple-600 dark:text-purple-400">{paginatedUsers.length}</span> of <span className="text-purple-600 dark:text-purple-400">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'person' : 'people'}</>
                    ) : (
                      <>Showing <span className="text-purple-600 dark:text-purple-400">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'person' : 'people'}</>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Hidden Users Section - Admin Only */}
          <HiddenUsersSection
            hiddenUsers={hiddenUsers}
            isAdmin={isAdmin}
            onUnhideUser={handleUnhideUser}
            loading={hiddenUsersLoading}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Memoized UserCard component to prevent unnecessary re-renders
 * Only re-renders when user data actually changes
 */
const MemoizedUserCard = React.memo(UserCard, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  const prevUser = prevProps.user;
  const nextUser = nextProps.user;
  
  // Compare key user properties that would affect rendering
  return (
    prevUser.uid === nextUser.uid &&
    prevUser.basicInfo?.firstName === nextUser.basicInfo?.firstName &&
    prevUser.basicInfo?.lastName === nextUser.basicInfo?.lastName &&
    prevUser.basicInfo?.currentRole === nextUser.basicInfo?.currentRole &&
    prevUser.basicInfo?.companyName === nextUser.basicInfo?.companyName &&
    prevUser.basicInfo?.location === nextUser.basicInfo?.location &&
    prevUser.basicInfo?.photoURL === nextUser.basicInfo?.photoURL &&
    prevUser.preferences?.jobSearchStatus === nextUser.preferences?.jobSearchStatus &&
    // Compare admin-related props
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.isHidden === nextProps.isHidden &&
    prevProps.onHideUser === nextProps.onHideUser &&
    prevProps.onUnhideUser === nextProps.onUnhideUser
  );
});

/**
 * SkeletonCard component for loading state
 * Mimics the structure of UserCard with animated placeholders
 */
const SkeletonCard = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 
                    p-6 h-full flex flex-col animate-pulse">
      {/* Profile Picture Skeleton */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>

      {/* User Information Skeleton */}
      <div className="text-center space-y-2 flex-grow flex flex-col">
        {/* Name */}
        <div className="min-h-[2.5rem] flex items-center justify-center">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-3/4"></div>
        </div>
        
        {/* Role */}
        <div className="min-h-[1.25rem] flex items-center justify-center">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-1/2"></div>
        </div>
        
        {/* Company */}
        <div className="min-h-[1.25rem] flex items-center justify-center">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-2/3"></div>
        </div>
        
        {/* Location */}
        <div className="min-h-[1.25rem] flex items-center justify-center">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mx-auto w-1/2"></div>
        </div>
        
        {/* Spacer */}
        <div className="flex-grow"></div>
        
        {/* Status Badge */}
        <div className="pt-3 mt-auto">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto w-24"></div>
        </div>
      </div>
    </div>
  );
};

export default PeopleDirectory;